// De tre tilstandene hver skjerm MÅ kunne vise: laster, feilet, tomt.
//
// Grunnen til at de bor her og ikke i hver skjerm: en skjerm som mangler
// feiltilstanden viser en tom liste når kallet feiler, og da kan ikke brukeren
// skille «du har ingen økter» fra «vi fikk ikke svar». Det er den vanligste
// måten et UI lyver på, og den er alltid utilsiktet.
import type { ApiFeil } from '../api/feil.js';
import { div, h, knapp, p, span } from './dom.js';
import { ikon, type Ikonnavn } from './ikoner.js';

/** Skjelett mens vi venter. Antyder formen på det som kommer, uten å påstå tall. */
export function laster(tekst = 'Henter …', linjer = 3): HTMLElement {
  const rot = div('tilstand tilstand-laster');
  rot.setAttribute('aria-busy', 'true');
  rot.append(span('tilstand-tekst', tekst));
  const skjelett = div('skjelett');
  for (let i = 0; i < linjer; i++) skjelett.append(div('skjelett-linje'));
  rot.append(skjelett);
  return rot;
}

/**
 * Feilboks. Viser tjenestens egen tekst, statuskoden (så drift kan gjenkjenne
 * den) og en «Prøv igjen» bare når en ny forsøk faktisk kan hjelpe.
 *
 * `endepunktMangler` får en egen ramme: det er ikke en driftsfeil, det er en
 * funksjon som ikke er rullet ut ennå, og det skal stå med de ordene.
 */
export function feilboks(feil: ApiFeil, påNytt?: () => void): HTMLElement {
  const mangler = feil.art === 'endepunktMangler';
  const rot = div(`tilstand tilstand-feil${mangler ? ' tilstand-mangler' : ''}`);
  rot.setAttribute('role', 'alert');

  const topp = div('tilstand-topp');
  topp.append(ikon(mangler ? 'info' : 'varsel'));
  topp.append(h(3, 'tilstand-tittel', mangler ? 'Funksjonen er ikke tilgjengelig ennå' : 'Kallet gikk ikke gjennom'));
  rot.append(topp);
  rot.append(p('tilstand-brød', feil.melding));

  const detalj = feil.status
    ? `HTTP ${feil.status} · ${feil.art}`
    : 'Ingen respons fra tjenesten';
  rot.append(p('tilstand-detalj', detalj));

  if (påNytt && feil.kanPrøvePåNytt) {
    rot.append(knapp('Prøv igjen', () => påNytt(), { variant: 'stille', ikon: ikon('last') }));
  }
  return rot;
}

export interface TomValg {
  ikon?: Ikonnavn;
  handling?: { tekst: string; gjør: () => void };
}

/**
 * Tomtilstand: dempet ikon, én linje veiledning, eventuelt en handling
 * (UX_DESIGNSYSTEM §8 — «ingen naken grå tekst»).
 */
export function tomt(tittel: string, veiledning: string, valg: TomValg = {}): HTMLElement {
  const rot = div('tilstand tilstand-tom');
  rot.append(ikon(valg.ikon ?? 'info'));
  rot.append(h(3, 'tilstand-tittel', tittel));
  rot.append(p('tilstand-brød', veiledning));
  if (valg.handling) {
    rot.append(knapp(valg.handling.tekst, valg.handling.gjør, { variant: 'primær', ikon: ikon('pluss') }));
  }
  return rot;
}

/**
 * Kunngjører for skjermleser. Én `aria-live`-region per app, ikke én per liste:
 * flere regioner konkurrerer, og da leses enten alt eller ingenting opp.
 *
 * `polite` med vilje — kunngjøringene her er kvitteringer («Økt meldt»), ikke
 * alarmer. Feil bruker `role=alert` i feilboksen over, som avbryter.
 */
export interface Kunngjører { rot: HTMLElement; si(melding: string): void }

export function lagKunngjører(): Kunngjører {
  const rot = div('kunngjøring');
  rot.setAttribute('aria-live', 'polite');
  rot.setAttribute('aria-atomic', 'true');
  return {
    rot,
    si(melding) {
      // Tømming først: samme tekst to ganger på rad leses ellers bare én gang,
      // og da tror brukeren at den andre handlingen ikke gikk gjennom.
      rot.textContent = '';
      globalThis.setTimeout(() => { rot.textContent = melding; }, 50);
    },
  };
}

/**
 * Kort kvittering nede i hjørnet. Forsvinner av seg selv — den er en bekreftelse
 * på noe brukeren nettopp gjorde, ikke informasjon hun må lese.
 */
export function kvittering(vert: HTMLElement, melding: string, variant: 'ok' | 'feil' = 'ok'): void {
  const boks = div(`kvittering kvittering-${variant}`);
  boks.append(ikon(variant === 'ok' ? 'sjekk' : 'varsel'), span('', melding));
  vert.append(boks);
  globalThis.setTimeout(() => { boks.classList.add('ut'); }, 3200);
  globalThis.setTimeout(() => { boks.remove(); }, 3600);
}

/** Fremdriftssøyle med tekstlig verdi — aldri farge alene. */
export function søyle(andel: number, variant = '', etikett?: string): HTMLElement {
  const a = Math.max(0, Math.min(1, Number.isFinite(andel) ? andel : 0));
  const rot = div(`søyle${variant ? ` søyle-${variant}` : ''}`);
  rot.setAttribute('role', 'img');
  rot.setAttribute('aria-label', etikett ?? `${Math.round(a * 100)} %`);
  const fyll = div('søyle-fyll');
  fyll.style.width = `${a * 100}%`;
  rot.append(fyll);
  return rot;
}
